import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { FileCheck, ExternalLink, Smartphone } from 'lucide-react';
import { useLeadEstimates } from '@/hooks/useEstimates';
import { useTechUsers } from '@/hooks/useReferenceData';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';
import { EstimateStatusBadge } from '@/components/estimates/EstimateStatusBadge';
import { cn } from '@/lib/utils';

interface JobEstimatesCardProps {
  job: { id: string; lead_id: string | null; status: string };
}

const OPEN_STATUSES = new Set(['sent', 'viewed']);

/**
 * Quotes raised on this job's lead, whether from the office (Send Estimate) or
 * from the technician's phone (tech_create_estimate). When the job is parked in
 * quote_sent, the open quote that parked it is called out.
 */
export function JobEstimatesCard({ job }: JobEstimatesCardProps) {
  const { data: estimates = [], isLoading } = useLeadEstimates(job.lead_id);
  const { data: techUsers } = useTechUsers();
  const { locale, timezone, formatMoney } = useCompanyLocale();

  if (!job.lead_id) return null;

  const waitingOn = job.status === 'quote_sent'
    ? estimates.find((e) => OPEN_STATUSES.has(e.status)) ?? null
    : null;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileCheck className="h-4 w-4 text-primary" />
            </div>
            <CardTitle className="text-lg">Estimates</CardTitle>
          </div>
          {estimates.length > 0 && (
            <span className="text-sm text-muted-foreground">{estimates.length} on this job</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <>
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </>
        ) : estimates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No quotes yet. Use "Send Estimate" above, or the technician can quote from the app on site.
          </p>
        ) : (
          estimates.map((estimate) => {
            const fromApp = !!estimate.created_by && !!techUsers?.has(estimate.created_by);
            const isWaiting = waitingOn?.id === estimate.id;
            return (
              <Link
                key={estimate.id}
                to={`/estimates/${estimate.id}`}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition-colors hover:bg-muted/40',
                  isWaiting && 'border-primary/40 bg-primary/5',
                )}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{estimate.estimate_number}</span>
                    <EstimateStatusBadge status={estimate.status} />
                    {fromApp && (
                      <Badge variant="outline" className="gap-1 text-[10px] px-1.5 py-0 text-info border-info/30">
                        <Smartphone className="h-3 w-3" />
                        Steddy app
                      </Badge>
                    )}
                    {isWaiting && (
                      <Badge variant="primary-soft" className="text-[10px] px-1.5 py-0">Waiting on customer</Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {formatDocDate(estimate.created_at, locale.shortDate, timezone)}
                    {estimate.valid_until ? ` · valid until ${formatDocDate(estimate.valid_until, locale.shortDate, timezone)}` : ''}
                    {estimate.sent_to_email ? ` · ${estimate.sent_to_email}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-sm font-semibold tabular-nums">{formatMoney(Number(estimate.estimate_amount) || 0)}</span>
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
              </Link>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
